import React from 'react';
import { __subscribeToToasts } from '../../constants/uiConstants';

type ToastType = 'success' | 'error' | 'info' | 'warning';

export type Toast = {
	id: string;
	message: string;
	type: ToastType;
	ttlMs?: number;
};

type ToastContextValue = {
	toasts: Toast[];
	show: (message: string, type?: ToastType, ttlMs?: number) => void;
	dismiss: (id: string) => void;
};

export const ToastContext = React.createContext<ToastContextValue | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [toasts, setToasts] = React.useState<Toast[]>([]);

	const dismiss = React.useCallback((id: string) => {
		setToasts(t => t.filter(x => x.id !== id));
	}, []);

	const show = React.useCallback((message: string, type: ToastType = 'info', ttlMs: number = 3500) => {
		const id = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
		const toast: Toast = { id, message, type, ttlMs };
		setToasts(t => [...t, toast]);
		if (ttlMs && ttlMs > 0) {
			setTimeout(() => dismiss(id), ttlMs);
		}
	}, [dismiss]);

	return (
			<ToastContext.Provider value={{ toasts, show, dismiss }}>
			{children}
			<ToastViewport toasts={toasts} onDismiss={dismiss} />
		</ToastContext.Provider>
	);
};

	// Bridge: subscribe to global notifications and render as toasts
	export const ToastBridge: React.FC = () => {
		const { show } = React.useContext(ToastContext)!;
		React.useEffect(() => {
			const unsub = __subscribeToToasts(({ message, type, duration }) => show(message, type, duration));
			return unsub;
		}, [show]);
		return null;
	};

const ToastViewport: React.FC<{ toasts: Toast[]; onDismiss: (id: string) => void }> = ({ toasts, onDismiss }) => {
		return (
			<div className="fixed top-4 right-4 left-4 md:left-auto z-[9999] space-y-2 pointer-events-none">
			{toasts.map(t => (
				<div
					key={t.id}
					className={
						`min-w-[260px] max-w-sm mx-auto md:mx-0 rounded-lg shadow-lg px-4 py-3 text-sm text-white pointer-events-auto ` +
						(t.type === 'success' ? 'bg-emerald-600' :
						 t.type === 'error' ? 'bg-red-600' :
						 t.type === 'warning' ? 'bg-amber-600' : 'bg-slate-700')
					}
					role="alert"
					aria-live={t.type === 'error' ? 'assertive' : 'polite'}
				>
					<div className="flex items-start gap-3">
						{/* Icon */}
						<div className="flex-shrink-0 pt-0.5">
							{t.type === 'success' && (
								<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
									<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
								</svg>
							)}
							{t.type === 'error' && (
								<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
									<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
								</svg>
							)}
							{t.type === 'warning' && (
								<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
									<path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
								</svg>
							)}
							{t.type === 'info' && (
								<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
									<path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
								</svg>
							)}
						</div>

						{/* Message */}
						<div className="flex-1 pr-2">{t.message}</div>

						{/* Close button - mobile-friendly touch target */}
						<button
							className="flex-shrink-0 opacity-80 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 rounded min-w-[24px] min-h-[24px] flex items-center justify-center"
							onClick={() => onDismiss(t.id)}
							aria-label="Dismiss notification"
						>
							<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
								<path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
							</svg>
						</button>
					</div>
				</div>
			))}
			</div>
	);
};
